import axios from "axios";

// Create an axios instance with base URL and auth header
const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || "http://localhost:8000",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Important: this enables sending cookies with cross-origin requests
});

// Helper function to get cookie by name
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

// Token management utilities
const TokenManager = {
  getAccessToken: () => localStorage.getItem("token"),
  getRefreshToken: () => localStorage.getItem("refresh"),
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem("token", accessToken);
    if (refreshToken) {
      localStorage.setItem("refresh", refreshToken);
    }
  },
  clearTokens: () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh");
  },
  hasValidTokens: () => {
    return !!(TokenManager.getAccessToken() && TokenManager.getRefreshToken());
  },
};

// Add token and CSRF token to requests
api.interceptors.request.use((config) => {
  // Add auth token if available
  const token = TokenManager.getAccessToken();
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  // Get CSRF token from cookie
  const csrfToken = getCookie("csrftoken");
  if (csrfToken) {
    config.headers["X-CSRFToken"] = csrfToken;
  }

  return config;
});

// Token refresh state management
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const logout = () => {
  TokenManager.clearTokens();
  // Use Vue Router if available, otherwise fallback to window.location
  if (window.app && window.app.$router) {
    window.app.$router.push("/login").catch(() => {
      window.location.href = "/login";
    });
  } else {
    window.location.href = "/login";
  }
};

const refreshAccessToken = async () => {
  const refreshToken = TokenManager.getRefreshToken();

  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  try {
    const response = await axios.post(
      `${api.defaults.baseURL}/api/token/refresh/`,
      { refresh: refreshToken },
      {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const newAccessToken = response.data.access;
    const newRefreshToken = response.data.refresh; // In case backend rotates refresh tokens

    TokenManager.setTokens(newAccessToken, newRefreshToken || refreshToken);

    return newAccessToken;
  } catch (error) {
    // Refresh token is invalid or expired
    TokenManager.clearTokens();
    throw error;
  }
};

// Handle response errors with proper token refresh logic
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Avoid infinite loops by checking if this is already a retry
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized (token expired)
    if (error.response && error.response.status === 401) {
      // URLs that should not trigger token refresh (login, token endpoints, etc.)
      const nonRefreshUrls = [
        "/api/token/",
        "/api/auth/login/",
        "/api/logout/",
        "/api/user/register/",
      ];

      // Check if this is a URL that shouldn't trigger refresh or if we don't have valid tokens
      const shouldNotRefresh = nonRefreshUrls.some((url) =>
        originalRequest.url.includes(url)
      );

      if (!TokenManager.hasValidTokens() || shouldNotRefresh) {
        logout();
        return Promise.reject(error);
      }

      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            originalRequest._retry = true;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      // Mark as refreshing and attempt token refresh
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newAccessToken = await refreshAccessToken();
        processQueue(null, newAccessToken);

        // Retry the original request with new token
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout user
        processQueue(refreshError, null);
        logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other error statuses
    if (error.response && error.response.status === 403) {
      // Check if this is a token-related 403 (some APIs return 403 for expired tokens)
      if (
        error.response.data &&
        error.response.data.detail &&
        error.response.data.detail.toLowerCase().includes("token")
      ) {
        // Treat like 401
        return api.interceptors.response.handlers[0].rejected({
          ...error,
          response: { ...error.response, status: 401 },
        });
      }
    }

    return Promise.reject(error);
  }
);

// Export token manager for use in components
export { TokenManager };

// Email Addresses API
export const getEmailAddresses = async (params = {}) => {
  const response = await api.get("/api/email-addresses/", {
    params: {
      page: params.page || 1,
      ...params,
    },
  });
  return response.data;
};

export const searchEmailAddresses = async (queryOrParams) => {
  // Support both old string parameter and new object parameter
  let params = {};
  if (typeof queryOrParams === "string") {
    params = { search: queryOrParams };
  } else {
    params = {
      search: queryOrParams.query || queryOrParams.search,
      page: queryOrParams.page || 1,
      ...queryOrParams,
    };
  }

  const response = await api.get("/api/email-addresses/", {
    params: params,
  });
  return response.data;
};

export const getEmailDetails = async (emailId) => {
  const response = await api.get(`/api/email-addresses/${emailId}/`);
  return response.data;
};

export const deleteEmailAddress = async (emailId) => {
  const response = await api.delete(`/api/email-addresses/${emailId}/`);
  return response.data;
};

// Messages API
export const getMessages = async (emailId, folder = "inbox", params = {}) => {
  const response = await api.get("/api/messages/", {
    params: {
      email_address: emailId,
      folder: folder,
      page: params.page || 1,
      page_size: params.page_size || 10,
      ...params,
    },
  });
  return response.data;
};

export const getMessage = async (messageId) => {
  const response = await api.get(`/api/messages/${messageId}/`);
  return response.data;
};

export const markMessageAsRead = async (messageId) => {
  const response = await api.post(`/api/messages/${messageId}/mark_read/`);
  return response.data;
};

export const moveMessageToFolder = async (messageId, folder) => {
  const response = await api.post(
    `/api/messages/${messageId}/move-to-folder/`,
    {
      folder: folder,
    }
  );
  return response.data;
};

export const deleteMessage = async (messageId) => {
  const response = await api.delete(`/api/messages/${messageId}/`);
  return response.data;
};

// Email Sending and Composition API
export const sendMessage = async (messageId) => {
  const response = await api.post(`/api/messages/${messageId}/send/`);
  return response.data;
};

export const replyToEmail = async (messageId, replyData) => {
  const formData = new FormData();

  // Add parent_message_id for the serializer (this is the key field the backend expects)
  formData.append("parent_message_id", messageId);

  // Add text fields - only include non-null, non-undefined values
  Object.keys(replyData).forEach((key) => {
    if (
      key !== "attachments" &&
      replyData[key] !== null &&
      replyData[key] !== undefined &&
      replyData[key] !== ""
    ) {
      formData.append(key, replyData[key]);
    }
  });

  // Add attachments if present
  if (replyData.attachments && replyData.attachments.length > 0) {
    replyData.attachments.forEach((file, index) => {
      // Make sure we're adding the actual File object
      if (file instanceof File) {
        formData.append("attachments", file);
      } else if (file.file && file.file instanceof File) {
        formData.append("attachments", file.file);
      } else {
        console.warn(
          `Attachment at index ${index} is not a valid File object:`,
          file
        );
      }
    });
  }

  const response = await api.post(
    `/api/messages/${messageId}/reply/`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

export const validateEmailAddresses = async (addresses) => {
  const response = await api.post("/api/email-validation/validate_addresses/", {
    addresses,
  });
  return response.data;
};

export const getUserEmailAddresses = async () => {
  const response = await api.get("/api/email-validation/user_addresses/");
  return response.data;
};

// Email Templates API
export const getEmailTemplates = async (params = {}) => {
  const response = await api.get("/api/email-templates/", { params });
  return response.data;
};

export const getEmailTemplate = async (templateId) => {
  const response = await api.get(`/api/email-templates/${templateId}/`);
  return response.data;
};

export const createEmailTemplate = async (templateData) => {
  const response = await api.post("/api/email-templates/", templateData);
  return response.data;
};

export const updateEmailTemplate = async (templateId, templateData) => {
  const response = await api.patch(
    `/api/email-templates/${templateId}/`,
    templateData
  );
  return response.data;
};

export const deleteEmailTemplate = async (templateId) => {
  const response = await api.delete(`/api/email-templates/${templateId}/`);
  return response.data;
};

// Email Signatures API
export const getEmailSignatures = async (params = {}) => {
  const response = await api.get("/api/email-signatures/", { params });
  return response.data;
};

export const getEmailSignature = async (signatureId) => {
  const response = await api.get(`/api/email-signatures/${signatureId}/`);
  return response.data;
};

export const createEmailSignature = async (signatureData) => {
  const response = await api.post("/api/email-signatures/", signatureData);
  return response.data;
};

export const updateEmailSignature = async (signatureId, signatureData) => {
  const response = await api.patch(
    `/api/email-signatures/${signatureId}/`,
    signatureData
  );
  return response.data;
};

export const deleteEmailSignature = async (signatureId) => {
  const response = await api.delete(`/api/email-signatures/${signatureId}/`);
  return response.data;
};

// Conversations API
export const getConversations = async (params = {}) => {
  const response = await api.get("/api/conversations/", { params });
  return response.data;
};

export const getConversation = async (conversationId) => {
  const response = await api.get(`/api/conversations/${conversationId}/`);
  return response.data;
};

export const markConversationAsRead = async (conversationId) => {
  const response = await api.post(
    `/api/conversations/${conversationId}/mark_all_read/`
  );
  return response.data;
};

export const deleteConversation = async (conversationId) => {
  const response = await api.delete(
    `/api/conversations/${conversationId}/delete_conversation/`
  );
  return response.data;
};

// Bulk Actions API
export const bulkMessageAction = async (actionData) => {
  const response = await api.post("/api/messages/bulk_action/", actionData);
  return response.data;
};

// Statistics API
export const getMessageStats = async () => {
  const response = await api.get("/api/messages/stats/");
  return response.data;
};

// Attachments API
export const getAttachment = async (attachmentId) => {
  const response = await api.get(`/api/attachments/${attachmentId}/`);
  return response.data;
};

export const downloadAttachment = async (attachmentId) => {
  const response = await api.get(`/api/attachments/${attachmentId}/download/`, {
    responseType: "blob",
  });
  return response;
};

// Search API
export const searchMessages = async (params = {}) => {
  const response = await api.get("/api/messages/search/", { params });
  return response.data;
};

export const searchConversations = async (params = {}) => {
  const response = await api.get("/api/conversations/search/", { params });
  return response.data;
};

export default api;
